import { AfterViewInit, Directive, ElementRef, EventEmitter, OnDestroy, Output, Input, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import {
  Subject,
  fromEvent,
  merge,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  takeUntil,
  withLatestFrom,
} from 'rxjs';

/**
 * This directive fires an event when click outside of the element where it is attached to is detected.
 * Used for example to close modals when user clicks outside of the modal content.
 *
 * @example
 * <code>
 *   <pre>
 *    <div (fcomClickOutside)="doSomething()">
 *      Some content
 *     </div>
 *   </pre>
 * </code>
 */
@Directive({
  selector: '[fcomClickOutside]',
  standalone: false,
})
export class ClickOutsideDirective implements AfterViewInit, OnDestroy {
  private elementRef = inject(ElementRef);
  private document = inject<Document>(DOCUMENT, { optional: true });

  /**
   * Tag name that you want to exclude from firing the output event
   */
  @Input() clickOutsideException = '';

  @Output()
  fcomClickOutside: EventEmitter<Event> = new EventEmitter();

  private ngDestroyed$ = new Subject();

  ngAfterViewInit(): void {
    if (!this.document) {
      return;
    }
    // setTimeout needed for the event not to occur when click inserts this directive in DOM
    setTimeout(() => {
      const click$ = fromEvent(this.document, 'click');

      const focusState$ = merge(
        fromEvent(this.elementRef.nativeElement, 'focusin').pipe(map(() => true)),
        fromEvent(this.elementRef.nativeElement, 'focusout').pipe(map(() => false))
      ).pipe(startWith(false), distinctUntilChanged());

      click$
        .pipe(
          withLatestFrom(focusState$),
          filter(([event, isFocused]) => {
            if (!(event.target instanceof Element) || isFocused) {
              return false;
            }
            return (
              !(this.elementRef.nativeElement as HTMLElement).contains(event.target) && !this.exceptionInPath(event)
            );
          }),
          takeUntil(this.ngDestroyed$)
        )
        .subscribe(([event]) => this.fcomClickOutside.emit(event));
    });
  }

  ngOnDestroy(): void {
    this.ngDestroyed$.next(true);
  }

  private exceptionInPath(event: Event): boolean {
    if (this.clickOutsideException === '') {
      return false; // dont even check if input is not given
    }

    if (!event.composedPath) {
      return true; // native clicks have this but test suite clicks dont
    }
    return !!event.composedPath().find((pathElem: Element) => pathElem.localName === this.clickOutsideException);
  }
}
